/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState } from "react";
import { useHistory } from "react-router-dom";

// @mui material components
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiSwitch from "components/VuiSwitch";
import GradientBorder from "examples/GradientBorder";

// Vision UI Dashboard assets
import radialGradient from "assets/theme/functions/radialGradient";
import palette from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";

// Authentication layout components
import CoverLayout from "layouts/authentication/components/CoverLayout";

// Wallet Services
import walletService from "services/walletService";
import storageService from "services/storageService";

// Images
import bgSignIn from "assets/login2.mp4";

// Icons
import { IoMail, IoKey } from "react-icons/io5";

function SignIn() {
  const history = useHistory();
  // Check if user has an existing account - if not, start on Private Key tab
  const hasAccount = storageService.hasUserAccount();
  const [activeTab, setActiveTab] = useState(hasAccount ? 0 : 1);
  const [rememberMe, setRememberMe] = useState(true);
  
  // Email/Password login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  // Private Key login
  const [privateKey, setPrivateKey] = useState("");
  const [showGenerateButton, setShowGenerateButton] = useState(true);
  
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError("");
    setSuccess("");
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    if (!email || !password) {
      setError("Please enter email and password");
      setLoading(false);
      return;
    }

    // Check if account exists
    if (!storageService.hasUserAccount()) {
      setError("No account found. Please use Private Key tab to create a wallet first.");
      setLoading(false);
      return;
    }

    // Verify email
    const savedEmail = storageService.loadUserEmail();
    if (savedEmail !== email) {
      setError("Invalid email or password");
      setLoading(false);
      return;
    }

    // Try to decrypt wallet with password
    const privateKeyWif = storageService.loadWallet(password);
    if (!privateKeyWif) {
      setError("Invalid email or password");
      setLoading(false);
      return;
    }

    // Verify private key is valid
    const address = await walletService.getAddressFromWif(privateKeyWif);
    if (!address) {
      setError("Wallet data corrupted. Please contact support.");
      setLoading(false);
      return;
    }

    // Save to session
    storageService.saveSession(privateKeyWif);
    
    // Redirect to dashboard
    setTimeout(() => {
      history.push("/dashboard");
    }, 500);
  };

  const handlePrivateKeyLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    if (!privateKey) {
      setError("Please enter your private key");
      setLoading(false);
      return;
    }

    // Validate private key
    const address = await walletService.getAddressFromWif(privateKey.trim());
    if (!address) {
      setError("Invalid private key format");
      setLoading(false);
      return;
    }

    // Save to session (no encryption, temporary)
    storageService.saveSession(privateKey.trim());
    
    setSuccess("Wallet loaded successfully!");
    
    // Redirect to dashboard
    setTimeout(() => {
      history.push("/dashboard");
    }, 500);
  };

  const handleGenerateWallet = async () => {
    setError("");
    setSuccess("");
    setLoading(true);
    
    try {
      // Generate new wallet
      const wallet = await walletService.generateNewWallet();
      
      setPrivateKey(wallet.privateKey);
      setShowGenerateButton(false);
      setSuccess(`New wallet generated! Address: ${wallet.address.substring(0, 20)}...`);
    } catch (error) {
      setError("Failed to generate wallet: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWallet = async () => {
    setError("");
    setLoading(true);

    if (!email || !password) {
      setError("Please enter email and password to save wallet");
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      setLoading(false);
      return;
    }

    if (!privateKey) {
      setError("No private key to save");
      setLoading(false);
      return;
    }

    try {
      // Validate private key
      const address = await walletService.getAddressFromWif(privateKey.trim());
      if (!address) {
        setError("Invalid private key");
        setLoading(false);
        return;
      }

      // Save encrypted wallet
      const saved = storageService.saveWallet(privateKey.trim(), password);
      if (!saved) {
        setError("Failed to save wallet");
        setLoading(false);
        return;
      }

      // Save email
      storageService.saveUserEmail(email);
      
      // Save to session
      storageService.saveSession(privateKey.trim());
      
      setSuccess("Wallet saved successfully! Redirecting...");
      
      // Redirect to dashboard
      setTimeout(() => {
        history.push("/dashboard");
      }, 1000);
    } catch (error) {
      setError("Error saving wallet: " + error.message);
      setLoading(false);
    }
  };

  return (
    <CoverLayout
      title="Welcome to Mytherra Wallet"
      color="white"
      description="Choose your login method"
      premotto=""
      motto=""
      image={bgSignIn}
    >
      {/* Login Method Tabs */}
      <VuiBox mb={3}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            background: "transparent",
            "& .MuiTabs-indicator": {
              display: "none",
            },
            "& .MuiTabs-flexContainer": {
              gap: "8px",
            },
          }}
        >
          <Tab
            label={
              <VuiBox display="flex" alignItems="center">
                <IoMail size="16px" style={{ marginRight: "6px" }} />
                Email & Password
              </VuiBox>
            }
            sx={{
              color: "#fff !important",
              fontSize: "12px",
              borderRadius: "8px !important",
              overflow: "hidden",
              background: activeTab === 0 
                ? "#00CED1" 
                : "linear-gradient(135deg, #004d4d 0%, #006666 100%)",
              "& .MuiTab-wrapper": {
                borderRadius: "8px !important",
              },
              "&.Mui-selected": {
                color: "#fff !important",
                background: "#00CED1",
                borderRadius: "8px !important",
              },
              "&:hover": {
                color: "#fff !important",
                background: activeTab === 0 
                  ? "#00CED1" 
                  : "linear-gradient(135deg, #005555 0%, #007777 100%)",
              },
            }}
          />
          <Tab
            label={
              <VuiBox display="flex" alignItems="center">
                <IoKey size="16px" style={{ marginRight: "6px" }} />
                Private Key
              </VuiBox>
            }
            sx={{
              color: "#fff !important",
              fontSize: "12px",
              borderRadius: "8px !important",
              overflow: "hidden",
              background: activeTab === 1 
                ? "#00CED1" 
                : "linear-gradient(135deg, #004d4d 0%, #006666 100%)",
              "& .MuiTab-wrapper": {
                borderRadius: "8px !important",
              },
              "&.Mui-selected": {
                color: "#fff !important",
                background: "#00CED1",
                borderRadius: "8px !important",
              },
              "&:hover": {
                color: "#fff !important",
                background: activeTab === 1 
                  ? "#00CED1" 
                  : "linear-gradient(135deg, #005555 0%, #007777 100%)",
              },
            }}
          />
        </Tabs>
      </VuiBox>

      {/* Email/Password Form */}
      {activeTab === 0 && (
        <VuiBox component="form" role="form" onSubmit={handleEmailLogin}>
          <VuiBox mb={2}>
            <VuiBox mb={1} ml={0.5}>
              <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                Email
              </VuiTypography>
            </VuiBox>
            <GradientBorder
              minWidth="100%"
              padding="1px"
              borderRadius={borders.borderRadius.lg}
              backgroundImage={radialGradient(
                palette.gradients.borderLight.main,
                palette.gradients.borderLight.state,
                palette.gradients.borderLight.angle
              )}
            >
              <VuiInput
                type="email"
                placeholder="Your email..."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fontWeight="500"
              />
            </GradientBorder>
          </VuiBox>
          <VuiBox mb={2}>
            <VuiBox mb={1} ml={0.5}>
              <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                Password
              </VuiTypography>
            </VuiBox>
            <GradientBorder
              minWidth="100%"
              borderRadius={borders.borderRadius.lg}
              padding="1px"
              backgroundImage={radialGradient(
                palette.gradients.borderLight.main,
                palette.gradients.borderLight.state,
                palette.gradients.borderLight.angle
              )}
            >
              <VuiInput
                type="password"
                placeholder="Your password..."
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={({ typography: { size } }) => ({
                  fontSize: size.sm,
                })}
              />
            </GradientBorder>
          </VuiBox>
          <VuiBox display="flex" alignItems="center">
            <VuiSwitch color="info" checked={rememberMe} onChange={handleSetRememberMe} />
            <VuiTypography
              variant="caption"
              color="white"
              fontWeight="medium"
              onClick={handleSetRememberMe}
              sx={{ cursor: "pointer", userSelect: "none" }}
            >
              &nbsp;&nbsp;&nbsp;&nbsp;Remember me
            </VuiTypography>
          </VuiBox>

          {error && (
            <VuiBox mt={2} mb={2} p={2} sx={{ background: "rgba(255, 0, 128, 0.1)", borderRadius: "8px" }}>
              <VuiTypography variant="caption" color="error">
                <Icon sx={{ mr: 1, verticalAlign: "middle" }}>error</Icon>
                {error}
              </VuiTypography>
            </VuiBox>
          )}

          {success && (
            <VuiBox mt={2} mb={2} p={2} sx={{ background: "rgba(0, 206, 209, 0.1)", borderRadius: "8px" }}>
              <VuiTypography variant="caption" color="success">
                <Icon sx={{ mr: 1, verticalAlign: "middle" }}>check_circle</Icon>
                {success}
              </VuiTypography>
            </VuiBox>
          )}

          <VuiBox mt={4} mb={1}>
            <VuiButton
              type="submit"
              color="info"
              fullWidth
              disabled={loading}
              sx={{
                background: "linear-gradient(135deg, #00CED1 0%, #0FA3A6 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #00CED1 0%, #0FA3A6 100%)",
                },
              }}
            >
              {loading ? "SIGNING IN..." : "SIGN IN"}
            </VuiButton>
          </VuiBox>
        </VuiBox>
      )}

      {/* Private Key Form */}
      {activeTab === 1 && (
        <VuiBox component="form" role="form" onSubmit={handlePrivateKeyLogin}>
          <VuiBox mb={2}>
            <VuiBox mb={1} ml={0.5}>
              <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                Private Key (WIF)
              </VuiTypography>
            </VuiBox>
            <GradientBorder
              minWidth="100%"
              padding="1px"
              borderRadius={borders.borderRadius.lg}
              backgroundImage={radialGradient(
                palette.gradients.borderLight.main,
                palette.gradients.borderLight.state,
                palette.gradients.borderLight.angle
              )}
            >
              <VuiInput
                type="password"
                placeholder="Enter or generate private key..."
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                fontWeight="500"
                multiline
                rows={3}
              />
            </GradientBorder>
          </VuiBox>

          {showGenerateButton && (
            <VuiBox mb={2}>
              <VuiButton
                color="warning"
                fullWidth
                onClick={handleGenerateWallet}
                sx={{
                  background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                  color: "#000",
                  "&:hover": {
                    background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                  },
                }}
              >
                <Icon sx={{ mr: 1 }}>add_circle</Icon>
                GENERATE NEW WALLET
              </VuiButton>
            </VuiBox>
          )}

          {!showGenerateButton && privateKey && (
            <VuiBox mb={2}>
              <VuiBox p={2} sx={{ background: "rgba(255, 0, 0, 0.2)", borderRadius: "8px", border: "2px solid #FF0000" }}>
                <VuiBox display="flex" alignItems="flex-start">
                  <Icon sx={{ color: "#FF0000", fontSize: "24px", mr: 1 }}>warning</Icon>
                  <VuiBox>
                    <VuiTypography variant="button" color="error" fontWeight="bold" display="block">
                      CRITICAL: SAVE YOUR PRIVATE KEY NOW!
                    </VuiTypography>
                    <VuiTypography variant="caption" color="error" display="block" mt={0.5}>
                      ⚠️ Copy and save your private key securely<br/>
                      ⚠️ You'll lose access to your funds if you lose it<br/>
                      ⚠️ Never share it with anyone
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
              </VuiBox>

              <VuiTypography variant="caption" color="white" display="block" mt={1} mb={2}>
                Optional: Save with email & password for easier login
              </VuiTypography>

              <VuiBox mb={2}>
                <GradientBorder
                  minWidth="100%"
                  padding="1px"
                  borderRadius={borders.borderRadius.lg}
                  backgroundImage={radialGradient(
                    palette.gradients.borderLight.main,
                    palette.gradients.borderLight.state,
                    palette.gradients.borderLight.angle
                  )}
                >
                  <VuiInput
                    type="email"
                    placeholder="Email (optional)"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fontWeight="500"
                  />
                </GradientBorder>
              </VuiBox>

              <VuiBox mb={2}>
                <GradientBorder
                  minWidth="100%"
                  padding="1px"
                  borderRadius={borders.borderRadius.lg}
                  backgroundImage={radialGradient(
                    palette.gradients.borderLight.main,
                    palette.gradients.borderLight.state,
                    palette.gradients.borderLight.angle
                  )}
                >
                  <VuiInput
                    type="password"
                    placeholder="Password (min 8 chars, optional)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fontWeight="500"
                  />
                </GradientBorder>
              </VuiBox>

              <VuiButton
                color="success"
                fullWidth
                onClick={handleSaveWallet}
                sx={{
                  background: "linear-gradient(135deg, #00FF00 0%, #00AA00 100%)",
                  color: "#000",
                  mb: 2,
                  "&:hover": {
                    background: "linear-gradient(135deg, #00FF00 0%, #00AA00 100%)",
                  },
                }}
              >
                <Icon sx={{ mr: 1 }}>save</Icon>
                SAVE & ENCRYPT WALLET
              </VuiButton>
            </VuiBox>
          )}

          <VuiBox p={2} mb={2} sx={{ background: "rgba(255, 215, 0, 0.1)", borderRadius: "8px" }}>
            <VuiBox display="flex" alignItems="flex-start">
              <Icon sx={{ color: "#FFD700", fontSize: "18px", mr: 1 }}>info</Icon>
              <VuiTypography variant="caption" color="text">
                <strong style={{ color: "#FFD700" }}>Security:</strong> Private key login is temporary. For permanent storage, save with email & password.
              </VuiTypography>
            </VuiBox>
          </VuiBox>

          {error && (
            <VuiBox mb={2} p={2} sx={{ background: "rgba(255, 0, 128, 0.1)", borderRadius: "8px" }}>
              <VuiTypography variant="caption" color="error">
                <Icon sx={{ mr: 1, verticalAlign: "middle" }}>error</Icon>
                {error}
              </VuiTypography>
            </VuiBox>
          )}

          {success && (
            <VuiBox mb={2} p={2} sx={{ background: "rgba(0, 206, 209, 0.1)", borderRadius: "8px" }}>
              <VuiTypography variant="caption" color="success">
                <Icon sx={{ mr: 1, verticalAlign: "middle" }}>check_circle</Icon>
                {success}
              </VuiTypography>
            </VuiBox>
          )}

          <VuiBox mt={4} mb={1}>
            <VuiButton
              type="submit"
              color="info"
              fullWidth
              disabled={loading || !privateKey}
              sx={{
                background: "linear-gradient(135deg, #00CED1 0%, #0FA3A6 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #00CED1 0%, #0FA3A6 100%)",
                },
              }}
            >
              {loading ? "ACCESSING WALLET..." : "ACCESS WALLET"}
            </VuiButton>
          </VuiBox>
        </VuiBox>
      )}
    </CoverLayout>
  );
}

export default SignIn;
